import CustomField from "components/common/CustomField";
import CustomLabel from "components/common/CustomLabel";
import DetailsPageTitle from "components/common/DetailsPageTitle";
import MultipleSelect from "components/common/MultipleSelect";
import SingleSelect from "components/common/SingleSelect";
import Spinners from "components/common/Spinners";
import TimePicker from "components/common/TimePicker";
import {
  defaultEndTime,
  defaultStartTime,
} from "components/constants/Constants";
import { ErrorMessage, Form, Formik } from "formik";
import { CircleX } from "lucide-react";
import { Calendar } from "primereact/calendar";
import { InputSwitch } from "primereact/inputswitch";
import React, { useEffect, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { getBranchList } from "store/actions/branchSlice";
import { formatDate } from "components/common/Utils";
import FileUpload from "components/common/FileUpload";
import { useNavigate, useSearchParams } from "react-router";
import FormButtons from "components/common/FormButtons";
import { addCouponSchema } from "components/common/Validation";
import { showAlert } from "store/reducers/alertReducer";
import {
  addCoupon,
  getCouponDetails,
  updateCoupon,
} from "store/actions/couponAction";
import { format } from "date-fns";
import { getSpaceListByType } from "store/actions/workspaceSlice";
import {
  couponRoleTypes,
  spaceTypeOptions,
} from "components/constants/DropdownOptions";
import { MultiSelect } from "primereact/multiselect";
import { getCustomerDropdownList } from "store/actions/customerSlice";
import { CalendarPrimaryIcon } from "components/common/SVGIcons";

const initialValues = {
  offerDetails: {
    name: "",
    nameAr: "",
    description: "",
    descriptionAr: "",
    imageUrl: "",
    imageArUrl: "",
  },
  roleType: 0,
  discountType: "percentage",
  platform: "",
  code: "",
  amount: null,
  startsAt: "",
  isNeverEnd: false,
  expiresAt: "",
  usageCount: null,
  type: "public",
  isMaxUsageOfCustomers: false,
  maxUsageOfCustomers: "",
  isMaxUsagePerCustomer: false,
  maxUsagePerCustomer: "",
  applicableBranches: [],
  spaceTypes: [],
  spaces: [],
  customers: [],
  isPrivate: false,
};

const AddEditCoupon = ({ mode }) => {
  const { t } = useTranslation();
  document.title =
    mode === "add"
      ? t("Add new coupon | Work Space")
      : t("Edit coupon | Work Space");

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");

  const { isLoading, loading, couponDetails } = useSelector(
    (state) => state.Coupons
  );
  const { customersList } = useSelector((state) => state.Customers);

  const { branchesList } = useSelector((state) => state.Branches);
  const { spaceLists } = useSelector((state) => state.Workspace);

  const [formValues, setFormValues] = useState(initialValues);
  const [datetime12h, setDateTime12h] = useState(null);
  const [startTime, setStartTime] = useState(defaultStartTime);
  const [endTime, setEndTime] = useState(defaultEndTime);
  const { brandInfo } = useSelector((state) => state.auth);
  const { primaryColor } = brandInfo?.theme?.themeColors  || {};

  useEffect(() => {
    const fetchBranchesList = () => {
      dispatch(getBranchList());
    };
    fetchBranchesList();

    const getDetails = () => {
      const payload = {
        id,
      };
      dispatch(getCouponDetails(payload));
    };
    dispatch(getCustomerDropdownList({ keyword: "" }));

    mode === "edit" && getDetails();
  }, []);

  const fetchSpaceListByType = (spaceTypes) => {
    const payload = {
      spaceTypes,
    };
    dispatch(getSpaceListByType(payload));
  };

  const handleCustomerSearch = (keyword) => {
    dispatch(getCustomerDropdownList({ keyword }));
  };

  useEffect(() => {
    if (mode === "edit" && couponDetails) {
      const {
        offerDeatails,
        discountType,
        platform,
        code,
        amount,
        startsAt,
        isNeverEnd,
        expiresAt,
        usageCount,
        type,
        limits,
        applicableBranches,
        spaceTypes,
        spaces,
        roleType,
        customers,
        isPrivate,
      } = couponDetails || {};

      fetchSpaceListByType(spaceTypes);
      // Update formValues using destructured values
      setFormValues((prevValues) => ({
        ...prevValues,
        offerDetails: {
          name: offerDeatails?.name,
          nameAr: offerDeatails?.nameAr,
          description: offerDeatails?.description,
          descriptionAr: offerDeatails?.descriptionAr,
          imageUrl: offerDeatails?.imageUrl ? [offerDeatails?.imageUrl] : [],
          imageArUrl: offerDeatails?.imageArUrl
            ? [offerDeatails?.imageArUrl]
            : [],
        },
        discountType,
        platform,
        code,
        amount,
        startsAt,
        isNeverEnd,
        expiresAt,
        usageCount,
        type,
        isMaxUsageOfCustomers: limits?.isMaxUsageOfCustomers,
        maxUsageOfCustomers: limits?.maxUsageOfCustomers,
        isMaxUsagePerCustomer: limits?.isMaxUsagePerCustomer,
        maxUsagePerCustomer: limits?.maxUsagePerCustomer,
        applicableBranches,
        spaceTypes,
        spaces,
        roleType,
        customers: customers || [],
        isPrivate,
      }));
    }
  }, [mode, couponDetails]);

  const handleOnSubmit = (values) => {
    const {
      offerDetails,
      discountType,
      platform,
      code,
      amount,
      startsAt,
      isNeverEnd,
      expiresAt,
      usageCount,
      type,
      isMaxUsageOfCustomers,
      maxUsageOfCustomers,
      isMaxUsagePerCustomer,
      maxUsagePerCustomer,
      applicableBranches,
      spaceTypes,
      spaces,
      roleType,
      customers,
      isPrivate,
    } = values;
    let data = new FormData();
    data.append("offerDeatails[name]", offerDetails.name || "");
    data.append("offerDeatails[nameAr]", offerDetails.nameAr || "");
    data.append("offerDeatails[description]", offerDetails.description || "");
    data.append(
      "offerDeatails[descriptionAr]",
      offerDetails.descriptionAr || ""
    );
    data.append("offerDeatails[imageUrl]", offerDetails?.imageUrl[0] || "");
    data.append("offerDeatails[imageArUrl]", offerDetails?.imageArUrl[0] || "");
    data.append("discountType", discountType);
    data.append("platform", "all");
    data.append("roleType", roleType);
    data.append("code", code);
    data.append("amount", amount);
    if (!isNeverEnd) {
      data.append("startsAt", startsAt);
      data.append("expiresAt", expiresAt);
    }
    data.append("isNeverEnd", isNeverEnd);
    // data.append("usageCount", usageCount);
    data.append("type", type);
    data.append("isPrivate", isPrivate || false);
    if (type === "private") {
      customers?.forEach((customer, index) => {
        data.append(`customers[${index}]`, customer);
      });
    }
    data.append("limits[isMaxUsageOfCustomers]", isMaxUsageOfCustomers);
    !isMaxUsageOfCustomers &&
      data.append("limits[maxUsageOfCustomers]", maxUsageOfCustomers);
    data.append("limits[isMaxUsagePerCustomer]", isMaxUsagePerCustomer);
    !isMaxUsagePerCustomer &&
      data.append("limits[maxUsagePerCustomer]", maxUsagePerCustomer);
    applicableBranches?.forEach((branchId, index) => {
      data.append(`applicableBranches[${index}]`, branchId);
    });
    spaceTypes?.forEach((type, index) => {
      data.append(`spaceTypes[${index}]`, type);
    });
    spaces?.forEach((spaceId, index) => {
      data.append(`spaces[${index}]`, spaceId);
    });

    if (mode === "add") {
      dispatch(addCoupon(data))
        .then((action) => {
          const { statusCode, success, message } = action?.payload;
          if (statusCode === 200 && success) {
            dispatch(showAlert({ text: message, type: "success" }));
            navigate(-1);
          } else {
            dispatch(showAlert({ text: message, type: "error" }));
          }
        })
        .catch((err) => {});
    } else if (mode === "edit") {
      data.append("id", id);

      dispatch(updateCoupon(data))
        .then((action) => {
          const { statusCode, success, message } = action?.payload;
          if (statusCode === 200 && success) {
            dispatch(showAlert({ text: message, type: "success" }));
            navigate(-1);
          } else {
            dispatch(showAlert({ text: message, type: "error" }));
          }
        })
        .catch((err) => {});
    }
  };

  const handleTimeSelect = (values, setFieldValue, datetime12h) => {
    if (!datetime12h)
      return dispatch(showAlert({ text: t("Select Date"), type: "error" }));
    const formattedDate = format(datetime12h, "yyyy-MM-dd'T'00:00:00.000'Z'");

    if (!values.startsAt) {
      setFieldValue("startsAt", formattedDate);
    } else {
      setFieldValue("expiresAt", formattedDate);
    }
  };

  const roomItemTemplate = (option) => {
    return (
      <div
        className="d-flex align-items-center gap-3 w-100"
        style={{
          border: "1px solid #D9D9D9",
          borderRadius: 10,
          padding: " 7px 10px",
        }}
      >
        <img
          src={option?.imageUrls[0]}
          // alt={option?.name}
          width={59}
          height={55}
          style={{ borderRadius: 10 }}
        />
        <div>
          <h2 className="fs_12 text_black">{option.name}</h2>

          <h2 className="fs_12 text_gray mt-1">
            <span>
              {option.cityId?.name}, {""}
            </span>
            <span>{option.branchId?.name}</span>
          </h2>
        </div>
      </div>
    );
  };

  return (
    <React.Fragment>
      <div className="page-content common_page add_workshop_page">
        {loading && mode === "edit" ? (
          <Spinners />
        ) : (
          <Container fluid>
            <Row>
              <DetailsPageTitle
                title={mode === "add" ? t("Add New Coupon") : t("Edit Coupon")}
              />
              <Formik
                initialValues={formValues}
                validationSchema={addCouponSchema}
                enableReinitialize={true}
                onSubmit={handleOnSubmit}
              >
                {({ values, setFieldValue, errors, touched }) => (
                  <Form>
                    <div className="common_wrapper">
                      <Row className="">
                        <Col lg={12}>
                          <div className="bg_white br_14 p_23_30 border_eee">
                            <h2 className="fs_20 mb-4">
                              {t("Basic Information")}
                            </h2>
                            <Row>
                              <Col lg={6}>
                                <div className="mb-3">
                                  <div className="form_field mb-0">
                                    <CustomLabel
                                      htmlFor="roleType"
                                      label={t("Coupon Type")}
                                    />
                                  </div>
                                  <SingleSelect
                                    options={couponRoleTypes}
                                    value={values?.roleType}
                                    placeholder={t("Select Coupon Type")}
                                    handleSingleSelectChange={(e) =>
                                      setFieldValue("roleType", e.value)
                                    }
                                    className={`${
                                      errors?.roleType && touched?.roleType
                                        ? "invalid"
                                        : ""
                                    }`}
                                    error={errors?.roleType}
                                  />
                                  {/* <div className="d-flex gap-3">
                                    <label class="custom-radio">
                                      <input
                                        type="radio"
                                        id="student"
                                        name="roleType"
                                        value={1}
                                        checked={values.roleType === 1}
                                        onChange={() =>
                                          setFieldValue("roleType", 1)
                                        }
                                      />
                                      <span class="checkmark"></span>
                                      {t("Student")}
                                    </label>

                                    <label class="custom-radio">
                                      <input
                                        type="radio"
                                        id="employee"
                                        name="roleType"
                                        value={2}
                                        checked={values.roleType === 2}
                                        onChange={() =>
                                          setFieldValue("roleType", 2)
                                        }
                                      />
                                      <span class="checkmark"></span>
                                      {t("Employee")}
                                    </label>
                                  </div> */}
                                </div>
                              </Col>
                              <Col lg={12}>
                                <div className="mb-3">
                                  <div className="d-flex gap-3">
                                    <label class="custom-radio">
                                      <input
                                        type="radio"
                                        id="fixed"
                                        name="discountType"
                                        value="fixed"
                                        checked={
                                          values.discountType === "fixed"
                                        }
                                        onChange={() =>
                                          setFieldValue("discountType", "fixed")
                                        }
                                      />
                                      <span class="checkmark"></span>
                                      {t("Fixed Amount Discount")}
                                    </label>
                                    {/* <div className="d-flex align-items-center">
                                      <input
                                        type="radio"
                                        id="fixed"
                                        name="discountType"
                                        value="fixed"
                                        checked={
                                          values.discountType === "fixed"
                                        }
                                        onChange={() =>
                                          setFieldValue("discountType", "fixed")
                                        }
                                      />
                                      <label htmlFor="fixed" className="ms-2">
                                        {t("Fixed Amount Discount")}
                                      </label>
                                    </div> */}
                                    <label class="custom-radio">
                                      <input
                                        type="radio"
                                        id="percentage"
                                        name="discountType"
                                        value="percentage"
                                        checked={
                                          values.discountType === "percentage"
                                        }
                                        onChange={() =>
                                          setFieldValue(
                                            "discountType",
                                            "percentage"
                                          )
                                        }
                                      />
                                      <span class="checkmark"></span>
                                      {t("Percentage Discount")}
                                    </label>
                                  </div>
                                </div>
                              </Col>
                              <Col lg={6}>
                                <div className="form_field">
                                  <CustomLabel
                                    htmlFor="code"
                                    label={t("Coupon Code")}
                                  />
                                  <CustomField
                                    name="code"
                                    type="text"
                                    placeholder={t("Coupon Code")}
                                    touched={touched}
                                    errors={errors}
                                  />
                                </div>
                              </Col>
                              <Col lg={6}>
                                <div className="form_field">
                                  <CustomLabel
                                    htmlFor="amount"
                                    label={t("Discount Value")}
                                  />
                                  <CustomField
                                    name="amount"
                                    type="text"
                                    placeholder={t("Discount Value")}
                                    touched={touched}
                                    errors={errors}
                                  />
                                </div>
                              </Col>
                            </Row>
                          </div>
                        </Col>
                      </Row>
                      <Row className="mt-4">
                        <Col lg={12}>
                          <div className="bg_white br_14 p_23_30 border_eee">
                            <h2 className="fs_20 mb-4">{t("Restrictions")}</h2>
                            <Row>
                              <Col lg={6}>
                                <div className="d-flex align-items-center mb-4">
                                  <InputSwitch
                                    checked={values.isMaxUsagePerCustomer}
                                    onChange={(e) => {
                                      setFieldValue(
                                        "isMaxUsagePerCustomer",
                                        !values.isMaxUsagePerCustomer
                                      );
                                    }}
                                  />
                                  <label
                                    htmlFor="isMaxUsagePerCustomer"
                                    className="ms-2"
                                  >
                                    {t("Unlimited")}
                                  </label>
                                </div>
                              </Col>
                              <Col lg={6}>
                                <div className="d-flex align-items-center mb-4">
                                  <InputSwitch
                                    checked={values.isMaxUsageOfCustomers}
                                    onChange={(e) => {
                                      setFieldValue(
                                        "isMaxUsageOfCustomers",
                                        !values.isMaxUsageOfCustomers
                                      );
                                    }}
                                  />
                                  <label
                                    htmlFor="isMaxUsageOfCustomers"
                                    className="ms-2"
                                  >
                                    {t("Unlimited")}
                                  </label>
                                </div>
                              </Col>
                              <Col lg={6}>
                                <div className="form_field">
                                  <CustomLabel
                                    htmlFor="maxUsagePerCustomer"
                                    label={t("Usage Per User")}
                                  />
                                  <CustomField
                                    name="maxUsagePerCustomer"
                                    type="text"
                                    placeholder={t("Usage Per User")}
                                    touched={touched}
                                    errors={errors}
                                    isReadOnly={values.isMaxUsagePerCustomer}
                                  />
                                </div>
                              </Col>
                              <Col lg={6}>
                                <div className="form_field">
                                  <CustomLabel
                                    htmlFor="maxUsageOfCustomers"
                                    label={t("Maximum Usage Limit")}
                                  />
                                  <CustomField
                                    name="maxUsageOfCustomers"
                                    type="text"
                                    placeholder={t("Maximum Usage Limit")}
                                    touched={touched}
                                    errors={errors}
                                    isReadOnly={values.isMaxUsageOfCustomers}
                                  />
                                </div>
                              </Col>
                            </Row>
                          </div>
                        </Col>
                      </Row>
                      <Row className="mt-4">
                        <Col lg={12}>
                          <div className="bg_white br_14 p_23_30 border_eee">
                            <h2 className="fs_20 mb-4">{t("Targeting")}</h2>
                            <Row>
                              <Col lg={6}>
                                <div className="form_field">
                                  <CustomLabel
                                    htmlFor="applicableBranches"
                                    label={t("Branches")}
                                  />
                                  <MultipleSelect
                                    value={values.applicableBranches}
                                    handleMultipleSelectChange={(e) =>
                                      setFieldValue("applicableBranches", e)
                                    }
                                    options={branchesList?.map((item) => ({
                                      name: item?.name,
                                      value: item?._id,
                                    }))}
                                    optionLabel="applicableBranches"
                                    placeholder={"Branches"}
                                    maxSelectedLabels={3}
                                    className={`${
                                      touched.applicableBranches &&
                                      errors.applicableBranches
                                        ? "invalid"
                                        : ""
                                    }`}
                                  />
                                  <ErrorMessage
                                    name={"applicableBranches"}
                                    component="div"
                                    className="text-danger"
                                  />
                                </div>
                              </Col>
                              <Col lg={6}>
                                <div className="form_field">
                                  <CustomLabel
                                    htmlFor="spaceTypes"
                                    label={t("Space Types")}
                                  />
                                  <MultipleSelect
                                    value={values.spaceTypes}
                                    handleMultipleSelectChange={(e) => {
                                      setFieldValue("spaceTypes", e);
                                      setFieldValue("spaces", []);
                                      fetchSpaceListByType(e);
                                    }}
                                    options={spaceTypeOptions}
                                    optionLabel="spaceTypes"
                                    placeholder={"Select Spaces"}
                                    maxSelectedLabels={3}
                                    className={`${
                                      touched.spaceTypes && errors.spaceTypes
                                        ? "invalid"
                                        : ""
                                    }`}
                                  />
                                  <ErrorMessage
                                    name={"spaceTypes"}
                                    component="div"
                                    className="text-danger"
                                  />
                                </div>
                              </Col>
                              <Col lg={6}>
                                <div className="form_field">
                                  <CustomLabel
                                    htmlFor="spaces"
                                    label={t("Products")}
                                  />
                                  <MultiSelect
                                    value={values.spaces}
                                    onChange={(e) =>
                                      setFieldValue("spaces", e.value)
                                    }
                                    options={spaceLists?.map((item) => ({
                                      name: item?.name,
                                      value: item?._id,
                                      cityId: item?.cityId,
                                      branchId: item?.branchId,
                                      imageUrls: item?.imageUrls,
                                    }))}
                                    optionLabel="name"
                                    display="chip"
                                    itemTemplate={roomItemTemplate}
                                    placeholder={"Products"}
                                    selectAllLabel={t("Select All")}
                                    className={`w-100 custom_multi_select coupon_product_select ${
                                      touched.spaces && errors.spaces
                                        ? "invalid"
                                        : ""
                                    }}`}
                                    filter
                                    filterPlaceholder={t("Search")}
                                    panelClassName="coupon_product_select"
                                  />
                                  {/* <MultipleSelect
                                    value={values.spaces}
                                    handleMultipleSelectChange={(e) =>
                                      setFieldValue("spaces", e)
                                    }
                                    options={spaceLists?.map((item) => ({
                                      name: item?.name,
                                      value: item?._id,
                                    }))}
                                    optionLabel="spaces"
                                    placeholder={"Products"}
                                    maxSelectedLabels={3}
                                    className={`${
                                      touched.spaces && errors.spaces
                                        ? "invalid"
                                        : ""
                                    }`}
                                  /> */}
                                  <ErrorMessage
                                    name={"spaces"}
                                    component="div"
                                    className="text-danger"
                                  />
                                </div>
                              </Col>
                              <Col lg={12}>
                                <div className="">
                                  <h2 className="fs_20 mb-4">
                                    {t("Date and Time")}
                                  </h2>
                                  <Row>
                                    <Col lg={6}>
                                      <div style={{ maxWidth: 500 }}>
                                        <Calendar
                                          id="calendar-12h"
                                          className="w-100"
                                          value={datetime12h}
                                          onChange={(e) =>
                                            setDateTime12h(e.value)
                                          }
                                          // showTime
                                          hourFormat="12"
                                          inline
                                          minDate={new Date()}
                                        />
                                      </div>
                                      <div className="d-flex align-items-start gap-4 ">
                                        <button
                                          type="button"
                                          onClick={() => {
                                            handleTimeSelect(
                                              values,
                                              setFieldValue,
                                              datetime12h
                                            );
                                          }}
                                          className="main_btn mt-3"
                                        >
                                          {t("Add Date")}
                                        </button>
                                      </div>
                                    </Col>
                                    <Col lg={6}>
                                      {!values.isNeverEnd && (
                                        <>
                                          <div className="form_field mb-0">
                                            <CustomLabel
                                              htmlFor="startsAt"
                                              label={t("Start On")}
                                            />
                                          </div>
                                          <div className="selected_date_time g_between mt-0">
                                            <div className="d-flex gap-2">
                                              <CalendarPrimaryIcon
                                                width={18}
                                                height={18}
                                                color={primaryColor}
                                              />
                                              <span>
                                                {/* {`${values.startsAt}`} */}
                                                {formatDate(values?.startsAt)}
                                              </span>
                                            </div>
                                            {values.startsAt && (
                                              <button
                                                type="button"
                                                className="transparent_btn"
                                                onClick={() => {
                                                  setFieldValue("startsAt", "");
                                                }}
                                              >
                                                <CircleX className="text_danger" />
                                              </button>
                                            )}
                                          </div>
                                          <ErrorMessage
                                            name={"startsAt"}
                                            component="div"
                                            className="text-danger"
                                          />
                                          <div className="form_field mb-0 mt-3">
                                            <CustomLabel
                                              htmlFor="expiresAt"
                                              label={t("Expires At")}
                                            />
                                          </div>
                                          <div className="selected_date_time g_between mt-0">
                                            <div className="d-flex gap-2">
                                              <CalendarPrimaryIcon
                                                width={18}
                                                height={18}
                                                color={primaryColor}
                                              />
                                              <span>
                                                {formatDate(values?.expiresAt)}
                                              </span>
                                            </div>
                                            {values.expiresAt && (
                                              <button
                                                type="button"
                                                className="transparent_btn"
                                                onClick={() => {
                                                  setFieldValue(
                                                    "expiresAt",
                                                    ""
                                                  );
                                                }}
                                              >
                                                <CircleX className="text_danger" />
                                              </button>
                                            )}
                                          </div>
                                          <ErrorMessage
                                            name={"expiresAt"}
                                            component="div"
                                            className="text-danger"
                                          />
                                        </>
                                      )}

                                      <div className="d-flex align-items-center mt-4">
                                        <InputSwitch
                                          checked={values.isNeverEnd}
                                          onChange={(e) => {
                                            const newValue = !values.isNeverEnd;
                                            setFieldValue(
                                              "isNeverEnd",
                                              newValue
                                            );
                                            if (newValue) {
                                              setFieldValue("startsAt", "");
                                              setFieldValue("expiresAt", "");
                                            }
                                          }}
                                        />
                                        <label
                                          htmlFor="isNeverEnd"
                                          className="ms-2"
                                        >
                                          {t("Never Ends")}
                                        </label>
                                      </div>
                                    </Col>
                                  </Row>
                                </div>
                              </Col>
                              <Col lg={12}>
                                <div className="bg_white br_14 p_23_30 border_eee mt-4">
                                  <h2 className="fs_20 mb-4">
                                    {t("Target Customers")}
                                  </h2>
                                  <Row>
                                    <Col lg={12}>
                                      <div className="d-flex align-items-center gap-3">
                                        <label class="custom-radio">
                                          <input
                                            type="radio"
                                            id="public"
                                            name="type"
                                            value="public"
                                            checked={values.type === "public"}
                                            onChange={() => {
                                              setFieldValue("type", "public");
                                              setFieldValue("customers", []);
                                            }}
                                          />
                                          <span class="checkmark"></span>
                                          {t("All Customers")}
                                        </label>
                                        <label class="custom-radio">
                                          <input
                                            type="radio"
                                            id="private"
                                            name="type"
                                            value="private"
                                            checked={values.type === "private"}
                                            onChange={() =>
                                              setFieldValue("type", "private")
                                            }
                                          />
                                          <span class="checkmark"></span>
                                          {t("Specific Customers")}
                                        </label>
                                      </div>
                                    </Col>
                                    {values?.type === "private" && (
                                      <Col lg={6}>
                                        <div className="form_field mt-3">
                                          <CustomLabel
                                            htmlFor="customers"
                                            label={t("Specific Customers")}
                                          />
                                          <MultiSelect
                                            value={values.customers}
                                            onChange={(e) => {
                                              setFieldValue(
                                                "customers",
                                                e.value
                                              );
                                            }}
                                            options={customersList?.map(
                                              (item) => ({
                                                name: `${item?.phone} - ${item?.name}`,
                                                value: item?._id,
                                              })
                                            )}
                                            optionLabel="name"
                                            display="chip"
                                            placeholder={"Specific Customers"}
                                            selectAllLabel={t("Select All")}
                                            className={`w-100 custom_multi_select`}
                                            filter
                                            filterPlaceholder={t(
                                              "Search by name, phone number"
                                            )}
                                            onFilter={(e) =>
                                              handleCustomerSearch(e.filter)
                                            }
                                          />
                                        </div>
                                      </Col>
                                    )}
                                  </Row>
                                </div>
                              </Col>
                              <Col lg={12}>
                                <div className="bg_white br_14 p_23_30 border_eee mt-4">
                                  <h2 className="fs_20 mb-4">{t("Offer")}</h2>

                                  <Row>
                                    <Col lg={12}>
                                      <div className="d-flex align-items-center mb-4">
                                        <InputSwitch
                                          checked={values.isPrivate}
                                          onChange={(e) => {
                                            setFieldValue(
                                              "isPrivate",
                                              !values.isPrivate
                                            );
                                          }}
                                        />
                                        <label
                                          htmlFor="isPrivate"
                                          className="ms-2"
                                        >
                                          {t("Private")}
                                        </label>
                                      </div>
                                    </Col>
                                    <Col lg={6} className="mt-4">
                                      <FileUpload
                                        uploadedFiles={
                                          values?.offerDetails?.imageUrl || []
                                        }
                                        setUploadedFiles={(files) =>
                                          setFieldValue(
                                            "offerDetails.imageUrl",
                                            files
                                          )
                                        }
                                        maxFiles={1}
                                        maxFileSize={0.3 * 1024 * 1024}
                                        acceptedFileTypes={[
                                          "image/png",
                                          "image/jpeg",
                                          "image/svg+xml",
                                        ]}
                                        requireSquare={false}
                                        requireRectangle={false}
                                        className={
                                          touched?.offerDetails?.imageUrl &&
                                          errors.offerDetails?.imageUrl
                                            ? "invalid"
                                            : ""
                                        }
                                      />
                                      <ErrorMessage
                                        name={"imageUrl"}
                                        component="div"
                                        className="text-danger"
                                      />
                                    </Col>
                                    <Col lg={6} className="mt-4">
                                      <FileUpload
                                        uploadedFiles={
                                          values?.offerDetails?.imageArUrl || []
                                        }
                                        setUploadedFiles={(files) =>
                                          setFieldValue(
                                            "offerDetails.imageArUrl",
                                            files
                                          )
                                        }
                                        maxFiles={1}
                                        maxFileSize={0.3 * 1024 * 1024}
                                        acceptedFileTypes={[
                                          "image/png",
                                          "image/jpeg",
                                          "image/svg+xml",
                                        ]}
                                        requireSquare={false}
                                        requireRectangle={false}
                                        className={
                                          touched?.offerDetails?.imageArUrl &&
                                          errors.offerDetails?.imageArUrl
                                            ? "invalid"
                                            : ""
                                        }
                                      />
                                    </Col>
                                    <Col lg={6}>
                                      <div className="form_field">
                                        <CustomLabel
                                          htmlFor="offerDetails.name"
                                          label={t("English Title")}
                                        />
                                        <CustomField
                                          name="offerDetails.name"
                                          type="text"
                                          placeholder={t("Enter English Title")}
                                          touched={touched.offerDetails}
                                          errors={errors.offerDetails}
                                        />
                                      </div>
                                    </Col>
                                    <Col lg={6}>
                                      <div className="form_field">
                                        <CustomLabel
                                          htmlFor="offerDetails.nameAr"
                                          label={t("Arabic Title")}
                                        />
                                        <CustomField
                                          name="offerDetails.nameAr"
                                          type="text"
                                          placeholder={t("Enter Arabic Title")}
                                          touched={touched.offerDetails}
                                          isArabic={true}
                                          errors={errors.offerDetails}
                                        />
                                      </div>
                                    </Col>
                                    <Col lg={6}>
                                      <div className="form_field">
                                        <CustomLabel
                                          htmlFor="offerDetails.description"
                                          label={t("English Description")}
                                        />
                                        <CustomField
                                          name="offerDetails.description"
                                          placeholder="Enter English Description"
                                          touched={touched.offerDetails}
                                          errors={errors.offerDetails}
                                          as="textarea"
                                        />
                                      </div>
                                    </Col>
                                    <Col lg={6}>
                                      <div className="form_field">
                                        <CustomLabel
                                          htmlFor="offerDetails.descriptionAr"
                                          label={t("Arabic Description")}
                                        />
                                        <CustomField
                                          name="offerDetails.descriptionAr"
                                          placeholder="Enter Arabic Description"
                                          touched={touched.offerDetails}
                                          errors={errors.offerDetails}
                                          as="textarea"
                                          isArabic={true}
                                        />
                                      </div>
                                    </Col>
                                  </Row>
                                </div>
                              </Col>
                              <div className="mt-5">
                                <FormButtons
                                  isLoading={isLoading}
                                  addBtnText={
                                    mode === "add" ? t("Add New") : t("Update")
                                  }
                                />
                              </div>
                            </Row>
                          </div>
                        </Col>
                      </Row>
                    </div>
                  </Form>
                )}
              </Formik>
            </Row>
          </Container>
        )}
      </div>
    </React.Fragment>
  );
};

export default AddEditCoupon;
